/**
 * Shared machinery for vanilla entity models: sheet textures, the lit entity
 * shader, and a ModelPart-style builder (cuboids unwrapped exactly like
 * ModelPart.Cube, grouped under pivots posed in pixel space).
 *
 * Space: every model lives under `modelRoot()`, whose scale (-1,-1,1)/16 is
 * vanilla's `scale(-1,-1,1)` plus the pixel->block division. Inside it parts
 * use vanilla numbers verbatim (y down, front = -z, rotations ZYX), and the
 * model's feet are at y = 24px, so the root sits 1.5 blocks above the feet.
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import { createEnvUniforms } from '../env';
import { LIGHTMAP_GLSL } from '../lightmap';

// --- textures -----------------------------------------------------------------

const texCache = new Map<string, THREE.Texture>();

/** Sheet texture under /mc (path without .png), loaded lazily; Nearest, sRGB, top-left origin. */
export function sheetTexture(path: string): THREE.Texture {
  let t = texCache.get(path);
  if (t) return t;
  t = new THREE.Texture();
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.flipY = false;
  t.colorSpace = THREE.SRGBColorSpace;
  loadInto(t, `mc/${path}.png`);
  texCache.set(path, t);
  return t;
}

export function loadInto(t: THREE.Texture, url: string, post?: (img: HTMLImageElement) => CanvasImageSource): void {
  const img = new Image();
  img.onload = () => {
    t.image = post ? post(img) : img;
    t.needsUpdate = true;
  };
  img.onerror = () => { /* missing sheet: the mesh just stays invisible (alpha 0 texture) */ };
  img.src = url;
}

// --- material -----------------------------------------------------------------

const VERT = /* glsl */ `
varying vec2 vUv;
varying vec3 vN;
varying vec3 vWp;
void main() {
  vUv = uv;
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWp = wp.xyz;
  vN = normalize(mat3(modelMatrix) * normal);
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

const FRAG = /* glsl */ `
precision highp float;
uniform sampler2D uMap;
uniform vec3 uTint;
uniform float uLight;
uniform float uSkyL;    // raw sky light 0..1 at the entity, or -1 = use uLight
uniform float uBlockL;
uniform float uDayLight;
uniform float uHurt;
uniform float uFlash;
uniform float uEmissive;
uniform float uFlat;
uniform float uAlphaCut;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uAmbientColor;
uniform float uGamma;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
varying vec2 vUv;
varying vec3 vN;
varying vec3 vWp;

${LIGHTMAP_GLSL}

void main() {
  vec4 tex = texture2D(uMap, vUv);
  if (tex.a < uAlphaCut) discard;
  vec3 albedo = tex.rgb * uTint;
  // vanilla overlays: hurt = 30% red, creeper flash = white, both before lighting
  albedo = mix(albedo, vec3(1.0, 0.0, 0.0), uHurt);
  albedo = mix(albedo, vec3(1.0), uFlash);
  vec3 n = normalize(vN);
  vec3 col;
  if (uFlat > 0.5) {
    // flat = unlit by the sun, but still dimmed by the surrounding light (1.0 leaves it unchanged)
    col = albedo * (0.62 + 0.38 * clamp(dot(n, normalize(vec3(0.35, 0.55, 0.8))), 0.0, 1.0)) * 1.15 * lightCurve(max(uLight, 0.1));
  } else if (uEmissive > 0.5) {
    col = albedo * 1.4;
  } else {
    // vanilla entity lighting: one lightmap sample at the entity (sky darkened
    // by time of day, warm block light, Brightness), times the fixed two-light
    // entity shade (Lighting.setupLevel). No sun, shadows or scene lights.
    float sky = uSkyL >= 0.0 ? uSkyL : uLight;
    float blk = uSkyL >= 0.0 ? uBlockL : 0.0;
    vec3 lm = max(vec3(lightCurve(sky * (0.22 + 0.78 * uDayLight))), blockLightTint(lightCurve(blk)));
    const vec3 L0 = vec3(0.16222142, 0.81110711, -0.56777498);
    const vec3 L1 = vec3(-0.16222142, 0.81110711, 0.56777498);
    float shade = min(1.0, 0.4 + 0.6 * (max(0.0, dot(n, L0)) + max(0.0, dot(n, L1))));
    vec3 light = lm * shade * 1.2;
    col = albedo * light;
    float fogDist = length(cameraPosition - vWp);
    col = mix(col, uFogColor, smoothstep(uFogNear, uFogFar, fogDist));
  }
#ifdef OUT_SRGB
  col = pow(col, vec3(1.0 / 2.2));
#endif
  gl_FragColor = vec4(col, 1.0);
}
`;

export interface EntityMat extends THREE.ShaderMaterial {
  uniforms: {
    uMap: THREE.IUniform<THREE.Texture | null>;
    uTint: THREE.IUniform<THREE.Color>;
    uLight: THREE.IUniform<number>;
    uSkyL: THREE.IUniform<number>;
    uBlockL: THREE.IUniform<number>;
    uHurt: THREE.IUniform<number>;
    uFlash: THREE.IUniform<number>;
    uEmissive: THREE.IUniform<number>;
    uFlat: THREE.IUniform<number>;
    uAlphaCut: THREE.IUniform<number>;
    [k: string]: THREE.IUniform;
  };
}

export interface MatOpts { tint?: THREE.ColorRepresentation; emissive?: boolean; flat?: boolean; srgbOut?: boolean; doubleSided?: boolean }

/** Per-instance lit material; env uniforms are shared by reference (sky updates all of them). */
export function entityMaterial(env: EnvUniforms, map: THREE.Texture, o: MatOpts = {}): EntityMat {
  const m = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    defines: o.srgbOut ? { OUT_SRGB: '' } : {},
    side: o.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
    uniforms: {
      uSunDir: env.uSunDir, uSunColor: env.uSunColor, uAmbientColor: env.uAmbientColor, uGamma: env.uGamma,
      uFogColor: env.uFogColor, uFogNear: env.uFogNear, uFogFar: env.uFogFar,
      uMap: { value: map },
      uTint: { value: new THREE.Color(o.tint ?? 0xffffff) },
      uLight: { value: 1 }, uSkyL: { value: -1 }, uBlockL: { value: 0 }, uDayLight: env.uDayLight,
      uHurt: { value: 0 }, uFlash: { value: 0 },
      uEmissive: { value: o.emissive ? 1 : 0 }, uFlat: { value: o.flat ? 1 : 0 },
      uAlphaCut: { value: 0.1 },
    },
  }) as EntityMat;
  return m;
}

let defaultEnv: EnvUniforms | null = null;
/** A constant full-daylight env for previews / tests that have no Sky. */
export function fallbackEnv(): EnvUniforms {
  return (defaultEnv ??= createEnvUniforms());
}

// --- model builder --------------------------------------------------------------

export interface CubeDef {
  tex: readonly [number, number];
  from: readonly [number, number, number];
  size: readonly [number, number, number];
  inflate?: number;
  mirror?: boolean;
}
export interface PartDef {
  cubes: readonly CubeDef[];
  pos?: readonly [number, number, number];
  rot?: readonly [number, number, number];
}

type V3 = [number, number, number];

/** Quad (4 corners, uvs, outward normal) wound CCW seen from outside. */
function pushQuad(
  pos: number[], nor: number[], uv: number[], idx: number[],
  pts: V3[], uvs: Array<[number, number]>, n: V3, tw: number, th: number,
): void {
  const ax = pts[1][0] - pts[0][0], ay = pts[1][1] - pts[0][1], az = pts[1][2] - pts[0][2];
  const bx = pts[2][0] - pts[1][0], by = pts[2][1] - pts[1][1], bz = pts[2][2] - pts[1][2];
  const c = (ay * bz - az * by) * n[0] + (az * bx - ax * bz) * n[1] + (ax * by - ay * bx) * n[2];
  const order = c < 0 ? [0, 3, 2, 1] : [0, 1, 2, 3];
  const base = pos.length / 3;
  for (const k of order) {
    pos.push(pts[k][0], pts[k][1], pts[k][2]);
    nor.push(n[0], n[1], n[2]);
    uv.push(uvs[k][0] / tw, uvs[k][1] / th);
  }
  idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

/** ModelPart.Cube: the six polygons with vanilla's vertex/uv layout, inflate and mirror. */
function addCube(c: CubeDef, tw: number, th: number, pos: number[], nor: number[], uv: number[], idx: number[]): void {
  const g = c.inflate ?? 0;
  let x0 = c.from[0] - g, y0 = c.from[1] - g, z0 = c.from[2] - g;
  let x1 = c.from[0] + c.size[0] + g, y1 = c.from[1] + c.size[1] + g, z1 = c.from[2] + c.size[2] + g;
  if (c.mirror) { const t = x0; x0 = x1; x1 = t; }
  const [w, h, d] = c.size;
  const [u, v] = c.tex;
  const v7: V3 = [x0, y0, z0], v0: V3 = [x1, y0, z0], v1: V3 = [x1, y1, z0], v2: V3 = [x0, y1, z0];
  const v3: V3 = [x0, y0, z1], v4: V3 = [x1, y0, z1], v5: V3 = [x1, y1, z1], v6: V3 = [x0, y1, z1];
  const f4 = u, f5 = u + d, f6 = u + d + w, f7 = u + d + w + w, f8 = u + d + w + d, f9 = u + d + w + d + w;
  const f10 = v, f11 = v + d, f12 = v + d + h;
  const sx = c.mirror ? -1 : 1;
  const poly = (pts: V3[], u1: number, vv1: number, u2: number, vv2: number, n: V3): void => {
    let uvs: Array<[number, number]> = [[u2, vv1], [u1, vv1], [u1, vv2], [u2, vv2]];
    if (c.mirror) { pts = pts.slice().reverse(); uvs = uvs.slice().reverse(); }
    pushQuad(pos, nor, uv, idx, pts, uvs, [n[0] * sx, n[1], n[2]], tw, th);
  };
  poly([v4, v3, v7, v0], f5, f10, f6, f11, [0, -1, 0]);
  poly([v1, v2, v6, v5], f6, f11, f7, f10, [0, 1, 0]);
  poly([v7, v3, v6, v2], f4, f11, f5, f12, [-1, 0, 0]);
  poly([v0, v7, v2, v1], f5, f11, f6, f12, [0, 0, -1]);
  poly([v4, v0, v1, v5], f6, f11, f8, f12, [1, 0, 0]);
  poly([v3, v4, v5, v6], f8, f11, f9, f12, [0, 0, 1]);
}

/** One pivot group holding a mesh of the part's cuboids. `mat` may be replaced later via mesh.material. */
export function buildPart(def: PartDef, tw: number, th: number, mat: THREE.Material): THREE.Group {
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], idx: number[] = [];
  for (const c of def.cubes) addCube(c, tw, th, pos, nor, uv, idx);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(idx);
  geo.computeBoundingSphere();
  const g = new THREE.Group();
  g.rotation.order = 'ZYX';
  if (def.pos) g.position.set(def.pos[0], def.pos[1], def.pos[2]);
  if (def.rot) g.rotation.set(def.rot[0], def.rot[1], def.rot[2]);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  g.add(mesh);
  return g;
}

/** Group that maps vanilla model space (px, y down, front -z, feet at y=24) to blocks at the feet. */
export function modelRoot(): THREE.Group {
  const g = new THREE.Group();
  g.scale.set(-1 / 16, -1 / 16, 1 / 16);
  g.position.y = 1.5;
  return g;
}

export const wrapPi = (a: number): number => {
  a = (a + Math.PI) % (Math.PI * 2);
  if (a < 0) a += Math.PI * 2;
  return a - Math.PI;
};

/** Apply the standard per-instance effect uniforms to every material. */
/**
 * World light sampler for entities: raw (sky, block) 0..15 at a position. Set
 * once by the game; when present, setEffects samples at the entity itself.
 */
export const ENTITY_LIGHT: { sample: ((x: number, y: number, z: number) => [number, number]) | null } = { sample: null };

export function setEffects(
  mats: readonly EntityMat[], light: number, hurt: boolean, flash: number, at?: { x: number; y: number; z: number },
): void {
  const smp = at && ENTITY_LIGHT.sample ? ENTITY_LIGHT.sample(at.x, at.y, at.z) : null;
  for (const m of mats) {
    m.uniforms.uLight.value = light;
    m.uniforms.uSkyL.value = smp ? smp[0] / 15 : -1;
    m.uniforms.uBlockL.value = smp ? smp[1] / 15 : 0;
    m.uniforms.uHurt.value = hurt ? 0.3 : 0;
    m.uniforms.uFlash.value = flash;
  }
}
